//@flow
import { Formik } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import connect from 'react-redux/es/connect/connect';
import Switch from 'react-switch';
import * as Yup from 'yup';
import FormSelectInput from '../../components/Inputs/FormSelectInput';
import SelectInput from '../../components/Inputs/SelectInput';
import Toast from '../../components/Toasts/Toast';
import { objectsConstants } from '../../constants/objects.constants';
import {
  hasRole,
  isPontoAtendimento,
  isSudo,
  Roles,
} from '../../helpers/permissions.helper';
import { clienteService } from '../../services/cliente.service';
import { pedidoDevolucaoService } from '../../services/pedidoDevolucao.service';
import { pontoAtendimentoService } from '../../services/pontoAtendimento.service';
import { produtoService } from '../../services/produto.service';
import { userService } from '../../services/user.service';
import TopoTitleComponente from '../Topo/TopoTitleComponente';
import CpfInput from '../../components/Inputs/CpfInput';
import FormInput from '../../components/Inputs/FormInput';
import { isValidCPF } from '../../components/Utils/cpfUtils';

const buildValidationSchema = (isSudo, isPontoAtendimento) => {
  return Yup.object().shape({
    cliente: isSudo
      ? Yup.object()
          .required('Cliente é obrigatório')
          .nullable()
      : Yup.mixed().nullable(), // não obrigatório

    pontoAtendimento: isPontoAtendimento
      ? Yup.mixed().nullable() // não obrigatório
      : Yup.object()
          .required('PA é obrigatório')
          .nullable(),
    responsavel: Yup.string()
      .nullable()
      .required('Nome do responsável é obrigatório'),
    cpfResponsavel: Yup.string()
      .nullable()
      .required('CPF  obrigatório')
      .test('cpf-valido', 'CPF inválido', (value) => {
        if (!value) return false;
        return isValidCPF(value);
      }),
    produtos: Yup.array()
      .min(1, 'Deve haver ao menos 1 produto no mapa')
      .of(
        Yup.object().shape({
          produto: Yup.object()
            .required('Produto obrigatório')
            .typeError('Produto obrigatório'),
          quantidade: Yup.number()
            .typeError('Limite deve ser um número')
            .min(1, 'Mínimo 1')
            .required('Obrigatório'),
          justificativa: Yup.string()
            .nullable()
            .required('Justificativa é obrigatória'),
          validade: Yup.date()
            .nullable()
            .transform((curr, orig) => (orig === '' ? null : curr))
            .min(
              new Date(new Date().setDate(new Date().getDate() + 30)),
              'Data de validade muito próxima (mínimo 30 dias para vencimento).'
            )
            .required('Validade obrigatória'),
        })
      ),
  });
};

class PedidoDevolucaoEdit extends React.PureComponent<Props> {
  constructor(props) {
    super(props);
    const user = userService.getCurrentUser();

    let cliente = null;
    let pontoAtendimento = null;

    if (isSudo()) {
      cliente = null;
    } else {
      cliente = user.cliente;

      if (isPontoAtendimento()) {
        pontoAtendimento = user.pontoAtendimento;
      }
    }

    this.state = {
      entity: {
        cliente,
        pontoAtendimento,
        produtos: [],
        urgente: false,
        justificativaRecusa: '',
      },
      atualizou: true,
    };
  }

  componentDidMount() {
    const id = _.get(this.props.match, 'params.id');
    if (id) {
      pedidoDevolucaoService.doGet(id).then((response) => {
        const pedido = response.data;

        // monta os produtos no formato usado pelo form
        pedido.produtos = (pedido.produtos || []).map((item) => ({
          produto: item.produto || null,
          idProduto:
            (item.produto && item.produto.id) || item.produtoId || null,
          lote: item.lote || '',
          quantidade: item.quantidade || 1,
          validade: item.validade || '',
          descricao: item.descricao || '',
          justificativa: item.justificativa || '',
        }));

        // se não for SUDO, seta o cliente com base no user logado
        const user = userService.getCurrentUser();
        if (!isSudo()) {
          pedido.cliente = user.cliente;
        } else {
          pedido.cliente = {
            codigoCliente: pedido.cliente && pedido.cliente.codigoCliente,
            nomeFantasia: pedido.cliente && pedido.cliente.nomeFantasia,
            label: pedido.cliente && pedido.cliente.label,
            codigoDepositante:
              pedido.cliente && pedido.cliente.codigoDepositante,
          };
        }

        pedido.pontoAtendimento = {
          id: pedido.pontoAtendimentoId,
          nome: pedido.pontoAtendimentoNome,
        };

        this.setState({ entity: pedido, atualizou: true });
      });
    } else {
      this.setState({ atualizou: true });
    }
  }

  render() {
    const { entity, atualizou } = this.state;
    const isEdit = !!this.state.entity.id;
    const dadosCarregados = !!atualizou;
    const validationSchema = buildValidationSchema(
      isSudo(),
      isPontoAtendimento()
    );
    const isFinalizado =
      (entity.status === 'APROVADO' || entity.status === 'RECUSADO') &&
      !isSudo();

    return (
      <React.Fragment>
        <TopoTitleComponente
          mainTitle="Pedido de Devolução"
          subTitle=" "
          canBack={true}
        />
        <Formik
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
          initialValues={entity}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            const payload = {
              id: values.id,
              pontoAtendimentoId: values.pontoAtendimento.id,
              pontoAtendimentoNome: values.pontoAtendimento.nome,
              produtos: values.produtos.map((p) => {
                const produto = p.produto || {};
                const descricao =
                  p.descricao ||
                  (produto.label ? produto.label : produto.descricao) ||
                  '';

                return {
                  produtoId: produto.id,
                  lote: p.lote,
                  quantidade: p.quantidade,
                  validade: p.validade,
                  descricao: descricao,
                  justificativa: p.justificativa,
                };
              }),

              cliente: values.cliente,
              responsavel: values.responsavel,
              cpfResponsavel: values.cpfResponsavel,
              status: values.status,
              urgente: values.urgente,
              justificativaRecusa: values.justificativaRecusa,
            };
            console.log(payload);

            pedidoDevolucaoService
              .doSave(payload)
              .then(() => {
                actions.setSubmitting(false);
                this.props.success({
                  message: 'Pedido de devolução salvo com sucesso.',
                });
                this.props.history.push('/pedidoDevolucao');
              })
              .catch((e) => {
                actions.setSubmitting(false);
                const mensagem =
                  _.get(e, 'response.data.detalhe') || 'Erro desconhecido.';
                this.props.error({ message: mensagem });
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex-column">
                <Toast />
                <div className="cardSeculus d-flex flex-column mb-0">
                  <div className="form-row">
                    {isSudo() && (
                      <div className="form-group col-md-6">
                        <FormSelectInput
                          disabled={isFinalizado}
                          name="cliente"
                          id="cliente"
                          label="Cliente"
                          placeholder="Selecione o cliente"
                          valueKey="codigoCliente"
                          labelKey="label"
                          required={true}
                          returnFullObject={true}
                          service={clienteService.findAll}
                          onChange={(name, value) => {
                            setFieldValue(name, value);
                            setFieldValue('produtos', []);
                          }}
                          value={values.cliente}
                          messageValidate={touched.cliente && errors.cliente}
                        />
                      </div>
                    )}
                    {!isPontoAtendimento() &&
                      values.cliente &&
                      dadosCarregados && (
                        <div className="form-group col-md-6">
                          <FormSelectInput
                            disabled={isFinalizado}
                            key={
                              (values.cliente &&
                                values.cliente.codigoCliente) ||
                              'pontoAtendimento'
                            }
                            name="pontoAtendimento"
                            id="pontoAtendimento"
                            label="Ponto de Atendimento"
                            placeholder="Selecione o PA"
                            valueKey="id"
                            labelKey="nome"
                            returnFullObject={true}
                            required={true}
                            parent={values.cliente}
                            service={pontoAtendimentoService.findAllByCliente}
                            onChange={(name, value) =>
                              setFieldValue(name, value)
                            }
                            value={values.pontoAtendimento}
                            messageValidate={errors.pontoAtendimento}
                          />
                        </div>
                      )}
                    <div className="form-group col-md-6">
                      <FormInput
                        noSize
                        label="Responsável"
                        id="responsavel"
                        disabled={isFinalizado}
                        type="text"
                        name={`responsavel`}
                        value={values.responsavel}
                        onChange={setFieldValue}
                        erroMensagem={errors.responsavel}
                        required
                      />
                    </div>
                    <div className="form-group col-md-6">
                      <CpfInput
                        placeholder="Digite o CPF"
                        erroMensagem={errors.cpfResponsavel}
                        id={'cpfResponsavel'}
                        name="cpfResponsavel"
                        value={values.cpfResponsavel}
                        onChange={setFieldValue}
                        noSize={true}
                        label={'CPF do responsável'}
                        disabled={isFinalizado}
                        required
                      />
                    </div>
                    {isEdit && (
                      <div className="form-group col-md-5">
                        <SelectInput
                          name="status"
                          id={'status'}
                          label="Status"
                          placeholder="Selecione o novo status do pedido"
                          valueKey="value"
                          labelKey="label"
                          noSize={true}
                          required={false}
                          returnFullObject={false}
                          isMulti={false}
                          searchable={false}
                          allowSelectAll={false}
                          options={objectsConstants.STATUS_PEDIDO_DEVOLUCAO}
                          messageValidate={errors.status}
                          disabled={isPontoAtendimento() || isFinalizado}
                          value={values.status}
                          className={`select-status ${values.status &&
                            values.status.toLowerCase()}`}
                          onChange={(name, value) => {
                            const statusAnterior = values.status;
                            const statusNovo = value;

                            const finalizando =
                              statusAnterior === 'CRIADO' &&
                              (statusNovo === 'APROVADO' ||
                                statusNovo === 'RECUSADO');

                            if (finalizando) {
                              confirmAlert({
                                customUI: ({ onClose }) => (
                                  <div className="custom-confirm-alert">
                                    <h2>Confirmação</h2>
                                    <p>
                                      Ao aprovar ou recusar um pedido, ele não
                                      poderá mais ser alterado. Deseja
                                      continuar?
                                    </p>
                                    <div className="d-flex justify-content-around mt-4">
                                      <button
                                        className="btn btn-secondary btn30 white"
                                        onClick={onClose}
                                      >
                                        Cancelar
                                      </button>
                                      <button
                                        className="btn btn-danger btn30"
                                        onClick={() => {
                                          setFieldValue(name, value);
                                          onClose();
                                        }}
                                      >
                                        Confirmar
                                      </button>
                                    </div>
                                  </div>
                                ),
                              });
                            } else {
                              setFieldValue(name, value);
                            }
                          }}
                        />
                      </div>
                    )}

                    <div className="form-group col-md-1 text-center">
                      <label className="label-nowrap">É urgente?</label>
                      <div>
                        <Switch
                          onChange={(value) => setFieldValue('urgente', value)}
                          checked={values.urgente || false}
                          onColor={'#0080ff'}
                          offColor={'#d9d9d9'}
                          height={30}
                          width={60}
                          handleDiameter={28}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          activeBoxShadow={'none'}
                          disabled={isFinalizado}
                        />
                      </div>
                    </div>

                    {values.status === 'RECUSADO' && (
                      <div className="form-group col-md-12">
                        <label htmlFor="justificativaRecusa">
                          Justificativa da Recusa
                        </label>
                        <textarea
                          className="form-control"
                          name="justificativaRecusa"
                          id="justificativaRecusa"
                          rows="3"
                          value={values.justificativaRecusa}
                          onChange={handleChange}
                          disabled={isFinalizado}
                        />
                      </div>
                    )}
                    <div className="form-group col-12 mt-3">
                      <h2>Itens para Devolução</h2>
                    </div>

                    {values.produtos.map((item, index) => (
                      <React.Fragment key={index}>
                        <div className="form-group col-md-6">
                          <FormSelectInput
                            key={`${(values.cliente &&
                              values.cliente.codigoCliente) ||
                              'no-cliente'}-${index}`}
                            name={`produtos[${index}].produto`}
                            id={`produto-${index}`}
                            label="Produto"
                            placeholder="Selecione o produto"
                            valueKey="id"
                            labelKey="label"
                            parent={values.cliente}
                            disabled={!values.cliente || isFinalizado}
                            noSize={true}
                            returnFullObject={true}
                            service={async (cliente) => {
                              const resp = await produtoService.findAll(
                                cliente
                              );

                              return { data: resp.data };
                            }}
                            onChange={(name, value) => {
                              setFieldValue(
                                `produtos[${index}].produto`,
                                value
                              );
                              setFieldValue(
                                `produtos[${index}].produtoId`,
                                value && value.id
                              );
                            }}
                            value={
                              item.produto ||
                              (item.idProduto
                                ? { id: item.idProduto, descricao: '' }
                                : null)
                            }
                            messageValidate={_.get(
                              errors,
                              `produtos[${index}].produto`
                            )}
                            required
                          />
                        </div>

                        <div className="form-group col-md-2">
                          <FormInput
                            noSize
                            label="Lote"
                            id={`produtos${index}lote`}
                            type="text"
                            name={`produtos[${index}].lote`}
                            value={item.lote}
                            onChange={setFieldValue}
                            disabled={isFinalizado}
                          />
                        </div>

                        <div className="form-group col-md-1">
                          <FormInput
                            noSize
                            label="Qtd."
                            type="number"
                            id={`produtos${index}quantidade`}
                            erroMensagem={_.get(
                              errors,
                              `produtos[${index}].quantidade`
                            )}
                            name={`produtos[${index}].quantidade`}
                            value={item.quantidade}
                            onChange={setFieldValue}
                            disabled={isFinalizado}
                          />
                        </div>
                        {/* {Object.keys(errors).length > 0 && (
                          <pre style={{ color: 'red' }}>
                            {JSON.stringify(errors, null, 2)}
                          </pre>
                        )} */}
                        <div className="form-group col-md-2">
                          <FormInput
                            noSize
                            label="Validade"
                            id={`produtos${index}validade`}
                            type="date"
                            name={`produtos[${index}].validade`}
                            erroMensagem={_.get(
                              errors,
                              `produtos[${index}].validade`
                            )}
                            value={item.validade}
                            onChange={setFieldValue}
                            disabled={isFinalizado}
                          />
                        </div>
                        {/* 
                        <div className="form-group col-md-6">
                          <FormInput
                            noSize
                            label="Descrição"
                            id={`produtos${index}descricao`}
                            type="text"
                            name={`produtos[${index}].descricao`}
                            value={item.descricao}
                            onChange={setFieldValue}
                            disabled={isFinalizado}
                          />
                        </div> */}

                        <div className="form-group col-md-5">
                          <FormInput
                            noSize
                            label="Justificativa"
                            id={`produtos${index}justificativa`}
                            type="text"
                            name={`produtos[${index}].justificativa`}
                            erroMensagem={_.get(
                              errors,
                              `produtos[${index}].justificativa`
                            )}
                            value={item.justificativa}
                            onChange={setFieldValue}
                            disabled={isFinalizado}
                            required
                          />
                        </div>

                        <div className="form-group col-md-1 d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={isFinalizado}
                            onClick={() => {
                              const novaLista = values.produtos.filter(
                                (_, i) => i !== index
                              );
                              setFieldValue('produtos', novaLista);
                            }}
                          >
                            Remover
                          </button>
                        </div>
                        <div
                          style={{
                            borderBottom: '1px solid #ccc',
                            margin: '2rem 0',
                            width: '100%',
                          }}
                        />
                      </React.Fragment>
                    ))}

                    <div className="form-group col-12 text-left">
                      <button
                        type="button"
                        className="btn btn-secondary btn30 green"
                        disabled={isFinalizado}
                        onClick={() => {
                          const novo = {
                            produto: null,
                            produtoId: null,
                            lote: '',
                            quantidade: 1,
                            validade: '',
                            descricao: '',
                            justificativa: '',
                          };
                          setFieldValue('produtos', [...values.produtos, novo]);
                        }}
                      >
                        + Adicionar Produto
                      </button>
                    </div>
                  </div>

                  <div className="d-flex flex-row">
                    <div className="col-6 col-lg text-center">
                      <button
                        type="button"
                        className="btn btn-secondary btn30 white"
                        disabled={isSubmitting}
                        onClick={() => this.props.history.goBack()}
                      >
                        Cancelar
                      </button>
                    </div>
                    {isEdit && (
                      <div className="col-6 col-lg text-center p-0 mt-lg-2 mb-1">
                        <button
                          type="button"
                          className="btn btn-danger btn30"
                          disabled={isSubmitting || isFinalizado}
                          onClick={() => {
                            confirmAlert({
                              customUI: ({ onClose }) => (
                                <div className="custom-confirm-alert">
                                  <h2>Confirmar exclusão</h2>
                                  <p>
                                    Você tem certeza que deseja excluir este
                                    Pedido de devolução?
                                  </p>
                                  <div className="d-flex justify-content-around mt-4">
                                    <button
                                      className="btn btn-secondary btn30 white"
                                      onClick={onClose}
                                    >
                                      Não
                                    </button>
                                    <button
                                      className="btn btn-danger btn30"
                                      onClick={async () => {
                                        try {
                                          await pedidoDevolucaoService.doDelete(
                                            values.id
                                          );
                                          this.props.success({
                                            message:
                                              'Pedido devolução excluído com sucesso.',
                                          });
                                          this.props.history.push(
                                            '/pedidoDevolucao'
                                          );
                                          onClose();
                                        } catch (e) {
                                          console.error(e);
                                          this.props.error({
                                            message:
                                              'Erro ao excluir o Ponto de Atendimento.',
                                          });
                                          onClose();
                                        }
                                      }}
                                    >
                                      Sim
                                    </button>
                                  </div>
                                </div>
                              ),
                            });
                          }}
                        >
                          Excluir
                        </button>
                      </div>
                    )}

                    <div className="col-6 col-lg text-center p-0 mt-lg-2 mb-1">
                      <button
                        type="submit"
                        className="btn btn-secondary btn30 green"
                        disabled={
                          isSubmitting ||
                          values.produtos.length === 0 ||
                          isFinalizado
                        }
                      >
                        {isEdit ? 'Salvar Alterações' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  const { user } = state.authentication;
  return { user };
}

const mapDispatch = ({
  alert: { success, error, clear },
  load: { loading },
}) => ({
  success: (msg) => success(msg),
  loading: (load: boolean) => loading({ load }),
  error: (msg) => error(msg),
  clear: () => clear(),
});

export default connect(
  mapStateToProps,
  mapDispatch,
  null,
  { forwardRef: true }
)(PedidoDevolucaoEdit);
